import { Ikon } from "../Ikon";
import { usePausUtanfor } from "../../lib/syns";

// Hero-illustrationen: agenten läser en exempelfaktura, markerar fält och stoppar en betalväg.
// Animationen är ren CSS (en gemensam cykel på 12 s, inga delays), se styles.css under "Agent visual".

type Falt = { namn: string; varde: string; h?: "h" | "h u" | "h bk"; klass?: string; k?: string };

const HUVUD: Falt[] = [
  { namn: "Fakturanr", varde: "10482", h: "h", k: "fakturanr" },
  { namn: "Fakturadatum", varde: "2026-04-14" },
  { namn: "Förfallodatum", varde: "2026-05-14", h: "h", k: "förfaller" },
  { namn: "Er referens", varde: "KS210 / Lindqv", h: "h u", k: "referens" },
];
const RADER: Falt[] = [
  { namn: "Konsulttjänster april, 32 h", varde: "32 000,00" },
  { namn: "Resor och logi", varde: "7 000,00" },
];
const SUMMOR: Falt[] = [
  { namn: "Netto", varde: "39 000,00" },
  { namn: "Moms 25 %", varde: "9 750,00", h: "h", k: "moms" },
  { namn: "Att betala", varde: "48 750,00 kr", h: "h", klass: "ptot", k: "belopp" },
];
const BETALNING: Falt[] = [
  { namn: "Bankgiro", varde: "5050-1055", h: "h bk", k: "bankgiro" },
  { namn: "Org.nr", varde: "556123-4567", h: "h", k: "org.nr" },
];

const Rad = ({ f }: { f: Falt }) => (
  <div className={["pf", f.klass, f.h].filter(Boolean).join(" ")}>
    <span>{f.namn}</span>
    {/* Fältetiketten tar samma plats i båda lagren men syns bara i spöklagret. */}
    {f.k ? <em className="ftag">{f.k}</em> : null}
    <b className={f.h === "h bk" ? "bank-flag" : undefined}>{f.varde}</b>
  </div>
);

// Fakturans innehåll. Renderas två gånger: en gång synligt och en gång som "spöklager" med
// genomskinlig text där bara markeringsrutorna syns. Samma markup gör att rutorna alltid
// hamnar exakt på fälten, oavsett bredd. Ändra därför bara här.
function FakturaInnehall({ ghost = false }: { ghost?: boolean }) {
  return (
    <div className={"pc" + (ghost ? " ghost" : "")}>
      <div className="ph">
        <div className="logo h"><i />Exempelleverantör AB</div>
        <div className="doc">Faktura</div>
      </div>
      <div className="pgrp">{HUVUD.map((f) => <Rad key={f.namn} f={f} />)}</div>
      <div className="prows">{RADER.map((f) => <Rad key={f.namn} f={f} />)}</div>
      <div className="pgrp">{SUMMOR.map((f) => <Rad key={f.namn} f={f} />)}</div>
      <div className="pgrp sist">
        {BETALNING.map((f) => <Rad key={f.namn} f={f} />)}
        <span className="redact r1" /><span className="redact r2" /><span className="redact r3" />
      </div>
    </div>
  );
}

const AVLAST: [string, string, string, string?][] = [
  ["leverantör", "Exempelleverantör AB", "0,99"],
  ["fakturanr", "10482", "0,99"],
  ["förfaller", "2026-05-14", "0,98"],
  ["referens", "KS210 / Lindqv", "0,62", "u"],
  ["belopp", "48 750,00 SEK", "0,99"],
  ["moms", "9 750,00 · 25 %", "0,99"],
  ["bankgiro", "5050-1055", "0,97", "bad"],
];

// Säkerheten visas både som siffra och som en liten stapel.
const sakerhet = (c: string) => Number(c.replace(",", "."));

function Avlast({ skel = false }: { skel?: boolean }) {
  return (
    <dl className={"kv" + (skel ? " skel" : " real")}>
      {AVLAST.map(([k, v, c, t]) => (
        <div key={k} className="kvr">
          <dt>{k}</dt><dd className={t}><span>{v}</span></dd>
          <dd className={"c" + (t === "u" ? " u" : "")}><i style={{ ["--v" as string]: sakerhet(c) }} /><span>{c}</span></dd>
        </div>
      ))}
    </dl>
  );
}

const KONTROLLER = ["Leverantören finns i registret", "Momsen stämmer mot raderna", "Ingen dubblett"];

export function HeroAgent() {
  // Pausa animationen när illustrationen är utanför skärmen (sparar batteri).
  const ref = usePausUtanfor<HTMLElement>();

  return (
    <figure ref={ref} className="agent" role="img" aria-label="Illustration: agenten läser en exempelfaktura, markerar ett osäkert fält och stoppar en betalväg som inte finns i registret.">
      <div className="abar" aria-hidden="true">
        <span className="pulse" />
        <span className="file">exempelfaktura_10482.pdf</span>
        <span className="st"><span className="s1">läser …</span><span className="s2">kontrollerar …</span><span className="s3">stoppad</span></span>
      </div>
      <div className="abody" aria-hidden="true">
        <div className="paper">
          <FakturaInnehall />
          <FakturaInnehall ghost />
          <div className="scan" />
          <span className="sikte" />
        </div>

        <div className="extract">
          <div className="card avlast">
            <h4><span>Avläst</span><span>säkerhet</span></h4>
            {/* Två lager: platshållare under, avläst text över som avtäcks i takt med skanningen. */}
            <div className="kvwrap">
              <Avlast skel />
              <Avlast />
              <span className="kvscan" />
            </div>
          </div>
          <div className="card">
            <h4><span>Kontroller</span><span>4 av ~40</span></h4>
            <ul className="checks">
              {KONTROLLER.map((k) => <li key={k}><i><Ikon namn="check" /></i>{k}</li>)}
              <li className="bad"><i><Ikon namn="x" /></i><span>Annan betalväg än registret<small>registret: bankgiro 123-4567</small></span></li>
            </ul>
          </div>
          <div className="verdict"><span className="d" /><span><b>Röd: handläggning.</b> Stoppas med orsaken angiven.</span></div>
        </div>
      </div>
    </figure>
  );
}
